"use client";
import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { cn } from "@/lib/utils";

type Direction = "TOP" | "LEFT" | "BOTTOM" | "RIGHT";

export function HoverBorderGradient({
  children,
  containerClassName,
  className,
  duration = 1,
  clockwise = true,
  isHovering,
  ...props
}: React.PropsWithChildren<
  {
    containerClassName?: string;
    className?: string;
    duration?: number;
    clockwise?: boolean;
    isHovering?: boolean;
  } & React.ButtonHTMLAttributes<HTMLButtonElement>
>) {
  const [direction, setDirection] = useState<Direction>("TOP");

  const rotateDirection = (currentDirection: Direction): Direction => {
    const directions: Direction[] = ["TOP", "LEFT", "BOTTOM", "RIGHT"];
    const currentIndex = directions.indexOf(currentDirection);
    const nextIndex = clockwise
      ? (currentIndex - 1 + directions.length) % directions.length
      : (currentIndex + 1) % directions.length;
    return directions[nextIndex];
  };

  const movingMap: Record<Direction, string> = {
    TOP: "radial-gradient(20.7% 50% at 50% 0%, #DBAD71 0%, rgba(219, 173, 113, 0) 100%)",
    LEFT: "radial-gradient(16.6% 43.1% at 0% 50%, #DBAD71 0%, rgba(219, 173, 113, 0) 100%)",
    BOTTOM:
      "radial-gradient(20.7% 50% at 50% 100%, #DBAD71 0%, rgba(219, 173, 113, 0) 100%)",
    RIGHT:
      "radial-gradient(16.2% 41.199999999999996% at 100% 50%, #DBAD71 0%, rgba(219, 173, 113, 0) 100%)",
  };

  useEffect(() => {
    if (!isHovering) {
      const interval = setInterval(() => {
        setDirection((prevState) => rotateDirection(prevState));
      }, duration * 1000);
      return () => clearInterval(interval);
    }
  }, [isHovering, duration]);

  // Extract height from props.style or use default
  const buttonStyle = {
    ...props.style,
    height: props.style?.height || 48, // Use passed height or default to 48
    border: "1px solid #313137",
    background: "black",
  };

  return (
    <button
      {...props}
      className={cn(
        "relative overflow-hidden rounded-lg cursor-pointer",
        containerClassName,
        className
      )}
      style={buttonStyle} // Use the combined style object
    >
      {/* Gradient border animation - HIDDEN when hovered */}
      <motion.div
        className="absolute inset-0 overflow-hidden rounded-lg"
        style={{
          filter: "blur(2px)",
        }}
        initial={{ background: movingMap[direction] }}
        animate={{
          opacity: isHovering ? 0 : 1,
          background: movingMap[direction],
        }}
        transition={{ ease: "linear", duration: duration ?? 1 }}
      />
      
      {/* Black background layer */}
      <div className="absolute bg-black inset-[2px] rounded-lg" />
      
      {/* Children content */}
      <div className="relative z-10 w-full h-full">
        {children}
      </div>
    </button>
  );
}